import { useEffect, useRef } from "react";
import {
  drawMonoBitmap,
  renderReceipt,
  toMonoBitmap,
} from "../../receipt/receiptRender";
import type { ReceiptData } from "../../receipt/receiptLayout";
import type { ReceiptTemplate } from "../../receipt/receiptTemplate";

/**
 * G-64 — what will actually come out of the printer.
 *
 * THE PREVIEW IS THE PRINT. It goes through `renderReceipt`, the same function
 * the print path uses, and is then reduced to pure black and white exactly as
 * `toMonoBitmap` will reduce it before the bytes are packed. Nothing here is a
 * stylised impression of a receipt: showing greys would flatter a logo that
 * prints as mud, and a shop would only find out on paper.
 */
export function ReceiptPreview({
  template,
  data,
  logo,
}: {
  template: ReceiptTemplate;
  data: ReceiptData;
  logo: HTMLImageElement | null;
}) {
  const host = useRef<HTMLDivElement | null>(null);
  const drawnOnce = useRef(false);

  useEffect(() => {
    const mount = host.current;
    if (!mount) return;

    const draw = () => {
      const shown = drawMonoBitmap(
        toMonoBitmap(renderReceipt(template, data, logo)),
      );
      shown.className = "receipt-preview-canvas";
      mount.replaceChildren(shown);
      drawnOnce.current = true;
    };

    // The first paint is immediate — a blank rectangle that fills in later
    // reads as a bug. After that, COALESCE: every keystroke in the message
    // field changes the template, and each redraw rasterises the whole ticket
    // and then walks ~165,000 pixels TWICE in JavaScript. On the low-powered
    // shop tablet this page is built for, that is felt while typing.
    if (!drawnOnce.current) {
      draw();
      return;
    }
    const timer = setTimeout(draw, 90);
    return () => clearTimeout(timer);
  }, [template, data, logo]);

  return <div className="receipt-preview" ref={host} />;
}
